import { useState, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronDown, AlertTriangle, Star, Settings, Search,
} from 'lucide-react';
import { useAppStore } from '@/stores/useAppStore';
import { useRole } from '@/hooks/useRole';
import { useNavCounts, type NavCounts } from '@/hooks/useNavCounts';
import { NavBadge } from '@/components/ui/NavBadge';
import {
  NAV_LAYOUTS, loadNavLayoutKind, COLLAPSED_STORAGE_KEY,
  type NavItem, type NavGroup,
} from './nav-layouts';
import { computeAttentionState } from './nav-attention';
import { usePinnedNav } from '@/hooks/usePinnedNav';
import { SidebarUserFooter } from './SidebarUserFooter';

// NS4 R3.3 — map nav-item route → (count-key, variant) for badge rendering.
// Only items that should show a count badge appear here; everything else
// renders without a badge.
const BADGE_FOR_ROUTE: Record<string, { key: keyof NavCounts; variant: 'info' | 'warn' | 'urgent' }> = {
  '/finance/bills':         { key: 'overdueBills',       variant: 'urgent' },
  '/workflows':             { key: 'pendingApprovals',   variant: 'warn' },
  '/hr':                    { key: 'pendingPto',         variant: 'warn' },
  '/contractors':           { key: 'missingW9',          variant: 'warn' },
  '/compliance':            { key: 'complianceDueSoon',  variant: 'info' },
  '/manager/approvals':     { key: 'pendingApprovals',   variant: 'warn' },
};

// SideNav is grouped by NAV_LAYOUTS (single canonical TASK_LAYOUT).
// Groups collapse state persists in localStorage. Sprint 1.5 moved Admin
// out of the sidebar to the topbar avatar dropdown (UserDropdown.tsx) so
// rarely-touched admin links don't compete for daily visual attention.
//
// Bottom of the sidebar is now SidebarUserFooter (avatar + name + role +
// 3-state theme toggle). The prior Help/Settings/Sign Out rows moved to:
//   - Help → topbar lifebuoy + Settings drilldown + dropdown (3 paths)
//   - Settings → Settings drilldown group + dropdown
//   - Sign Out → topbar avatar dropdown last item

function loadCollapsedGroups(): Set<string> | null {
  try {
    const raw = localStorage.getItem(COLLAPSED_STORAGE_KEY);
    if (!raw) return null;
    return new Set(JSON.parse(raw));
  } catch { return null; }
}

function saveCollapsedGroups(collapsed: Set<string>) {
  try { localStorage.setItem(COLLAPSED_STORAGE_KEY, JSON.stringify([...collapsed])); } catch { /* */ }
}

const navLinkCls = (isActive: boolean) =>
  `flex items-center gap-3 px-3 py-2 rounded-[var(--radius-md)] text-sm font-medium transition-colors ${
    isActive
      ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
      : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700'
  }`;

// `variant` lets the SAME sidebar render in two places so desktop + mobile are
// congruent (2026-06-02): 'desktop' = the sticky left rail (lg+ only); 'drawer'
// = inside the mobile slide-in panel (MobileNavDrawer). onNavigate fires when a
// link is tapped so the drawer can close itself. Desktop passes neither.
export function SideNav({ variant = 'desktop', onNavigate }: { variant?: 'desktop' | 'drawer'; onNavigate?: () => void } = {}) {
  const { entities } = useAppStore();
  const { hasMinRole, role } = useRole();
  const location = useLocation();
  const { counts } = useNavCounts();
  const { pinnedRoutes, isPinned, togglePin } = usePinnedNav();

  // Active nav layout kind. The layout system was simplified to a single
  // canonical TASK_LAYOUT in NS4 (see nav-layouts.ts), but the kind hook
  // is preserved so any future re-introduction of multiple layouts can
  // wire back in without touching SideNav. Custom event from Settings is
  // still observed to trigger a re-render after a no-op save.
  const [layoutKind, setLayoutKind] = useState(() => loadNavLayoutKind());
  useEffect(() => {
    const onChange = () => setLayoutKind(loadNavLayoutKind());
    window.addEventListener('nexus:nav-layout-changed', onChange);
    return () => window.removeEventListener('nexus:nav-layout-changed', onChange);
  }, []);

  const groups: NavGroup[] = NAV_LAYOUTS[layoutKind];
  const isMultiEntity = entities.length > 1;

  // Sidebar P1 (2026-05-20): derive the smart-attention group from the
  // same counts that drive per-item badges. computeAttentionState handles
  // the 3-state classification (calm/background/active) + filters
  // zero-count rows. Keeps the rendering loop below trivial.
  // Sidebar P2 role-filter (2026-05-21): pass role so attention rows
  // are filtered per backend role-gate. Manager + employee see no items.
  const attention = useMemo(() => computeAttentionState(counts, role), [counts, role]);
  // Home Redesign Step 2a (2026-06-17): the attention surface collapsed from an
  // expandable 6-row list to a single 3-state PILL that links to Home. The rows
  // themselves now live as cards on the Home page (Step 1 `homeCard` / Home
  // "Needs attention" strip) + keep their per-item badges in the regular groups
  // (Bills/Contractors/HR/Compliance/Workflows via BADGE_FOR_ROUTE). So the pill
  // is a triage entry-point, not the only path to any item — nothing is lost.
  // Badge shows the summed pending quantity, or the row count when the only
  // signal is a non-quantity row (e.g. AI budget %, which is excludeFromTotal).
  const attentionBadge = attention.total > 0 ? attention.total : attention.items.length;

  // Filter items per minRole AND per whenMultiEntity flag. Drop groups
  // where ALL items got filtered away — no point in showing an empty
  // group header.
  // Nav declutter (2026-06-15): the 'settings' group stays in TASK_LAYOUT (so
  // its sub-pages remain Cmd+P-searchable + listed on the /settings index) but
  // is NOT rendered as a sidebar group — it collapses to a single Settings link
  // pinned in the footer. Excluded here.
  const visibleGroups = useMemo(() => groups
    .filter(g => g.key !== 'settings')
    // 5-section consolidation (2026-06-18): managerOnly groups (the Manager
    // portal) render ONLY for a pure-manager login. Owners/admins don't need it
    // (they have /workflows + /hr); its routes stay ⌘P-searchable in TASK_LAYOUT.
    .filter(g => !g.managerOnly || role === 'manager')
    .map(g => ({
      ...g,
      items: g.items.filter(n => {
        if (n.minRole && !hasMinRole(n.minRole)) return false;
        if (n.whenMultiEntity && !isMultiEntity) return false;
        return true;
      }),
    }))
    .filter(g => g.items.length > 0), [groups, hasMinRole, isMultiEntity]);

  // Sidebar P2b (2026-05-21): build the Pinned group from localStorage-
  // persisted pinned routes. Iterate visibleGroups (post role-filter) so
  // pins for routes the user can't access don't appear in the pinned
  // group. Preserve user insertion order via the order in pinnedRoutes Set.
  const pinnedItems = useMemo(() => {
    if (pinnedRoutes.size === 0) return [];
    // Build lookup map: route → NavItem (only items the user can see)
    const lookup = new Map<string, NavItem>();
    for (const g of visibleGroups) {
      for (const it of g.items) lookup.set(it.to, it);
    }
    const out: NavItem[] = [];
    for (const route of pinnedRoutes) {
      const item = lookup.get(route);
      if (item) out.push(item);
    }
    return out;
  }, [visibleGroups, pinnedRoutes]);

  // Collapse state. Initial = saved set OR default-derived from
  // defaultOpen flags. Per-group toggle persists immediately. Layout
  // change clears the saved set (see nav-layouts.ts).
  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    const saved = loadCollapsedGroups();
    if (saved) return saved;
    return new Set(groups.filter(g => !g.defaultOpen && g.label).map(g => g.key));
  });
  // When the user changes layout, re-derive defaults.
  useEffect(() => {
    const saved = loadCollapsedGroups();
    if (saved) { setCollapsed(saved); return; }
    setCollapsed(new Set(groups.filter(g => !g.defaultOpen && g.label).map(g => g.key)));
  }, [layoutKind, groups]);

  // Auto-expand the group containing the active route on navigation,
  // so you're never staring at a collapsed group while ON one of its
  // items. Doesn't persist the auto-expand to storage.
  const activePath = location.pathname;
  const activeGroupKey = visibleGroups.find(g =>
    g.items.some(i => i.to === activePath
      || (i.to !== '/' && activePath.startsWith(i.to + '/'))
      || (i.to === '/' && activePath === '/'))
  )?.key;

  useEffect(() => {
    if (!activeGroupKey) return;
    if (collapsed.has(activeGroupKey)) {
      const next = new Set(collapsed);
      next.delete(activeGroupKey);
      setCollapsed(next);
      // Don't save to storage — this auto-expand is contextual only
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGroupKey]);

  const toggleGroup = (key: string) => {
    const next = new Set(collapsed);
    if (next.has(key)) next.delete(key); else next.add(key);
    setCollapsed(next);
    saveCollapsedGroups(next);
  };

  return (
    <aside
      className={
        variant === 'drawer'
          // Fills the mobile drawer panel (panel supplies width + position).
          ? 'flex flex-col w-full h-full bg-white dark:bg-slate-800'
          // Desktop: sticky left rail, hidden below lg.
          : 'hidden lg:flex flex-col w-60 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 h-[calc(100dvh-64px)] sticky top-16'
      }
      data-testid={variant === 'drawer' ? 'sidenav-drawer' : 'sidenav'}
    >
      <nav className="flex-1 px-3 py-3 space-y-3 overflow-y-auto">
        {/* P3 (2026-06-16): discoverable command palette. Fires the same
            `nexus:command-palette-open` event as Cmd/Ctrl+P (useKeyboardShortcuts).
            The ⌘P hint shows on desktop only; in the mobile drawer this doubles
            as the search affordance. onNavigate closes the drawer first so the
            palette isn't stacked behind it. */}
        <button
          type="button"
          onClick={() => { onNavigate?.(); window.dispatchEvent(new CustomEvent('nexus:command-palette-open')); }}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm text-slate-400 hover:text-slate-600 hover:border-slate-300 dark:hover:text-slate-300 transition-colors"
          aria-label="Search pages"
        >
          <Search className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-left">Search pages…</span>
          <kbd className="hidden lg:inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-500">⌘P</kbd>
        </button>
        {/* Home Redesign Step 2a (2026-06-17): smart attention as a single
            3-state PILL (was a 6-row expandable list — the operator's "left
            menu cluttered" finding). Hides when calm. Background = amber pill.
            Active = red pill + dot. Links to Home, where the alerts now render
            as cards (Step 1) + keep their per-item badges in the groups below.
            data-testid stays 'sidenav-attention' so the structure spec tracks it. */}
        {attention.state !== 'calm' && (
          <NavLink
            to="/"
            end
            onClick={onNavigate}
            data-testid="sidenav-attention"
            title={`${attention.total} item${attention.total === 1 ? '' : 's'} need attention — open Home`}
            aria-label={`Needs attention: ${attention.total} item${attention.total === 1 ? '' : 's'}. Opens Home.`}
            className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
              attention.state === 'active'
                ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100 dark:border-red-900/50 dark:text-red-300 dark:bg-red-950/30'
                : 'border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 dark:border-amber-900/50 dark:text-amber-300 dark:bg-amber-950/30'
            }`}
          >
            {attention.state === 'active' && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden />
            )}
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span className="flex-1 text-left">Needs attention</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              attention.state === 'active'
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
            }`}>
              {attentionBadge > 99 ? '99+' : attentionBadge}
            </span>
          </NavLink>
        )}
        {/* Sidebar P2b (2026-05-21): Pinned group. Renders only when the
            user has at least one pinned route. Sits above functional
            groups (below the attention group) so power users get their
            most-used items one click away. */}
        {pinnedItems.length > 0 && (
          <div key="pinned">
            <div className="flex items-center justify-between w-full px-3 py-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
              <span className="flex items-center gap-1.5">
                <Star className="w-3 h-3" fill="currentColor" />
                <span>Pinned</span>
              </span>
            </div>
            <div className="space-y-0.5 mt-1">
              {pinnedItems.map(({ to, icon: Icon, label, hint }) => {
                const badgeConfig = BADGE_FOR_ROUTE[to];
                const badgeCount = badgeConfig ? counts[badgeConfig.key] : 0;
                return (
                  <div key={`pinned-${to}`} className="group/navrow relative">
                    <NavLink
                      to={to}
                      end={to === '/'}
                      onClick={onNavigate}
                      title={hint}
                      className={({ isActive }) => navLinkCls(isActive)}
                    >
                      <Icon className="w-5 h-5 shrink-0" />
                      <span className="flex-1">{label}</span>
                      {badgeConfig && (
                        <NavBadge count={badgeCount} variant={badgeConfig.variant} />
                      )}
                    </NavLink>
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(to); }}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-amber-500 hover:text-amber-600 opacity-0 group-hover/navrow:opacity-100 focus:opacity-100 transition-opacity"
                      aria-label={`Unpin ${label}`}
                      title="Unpin"
                    >
                      <Star className="w-3.5 h-3.5" fill="currentColor" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {visibleGroups.map((group) => {
          const isCollapsed = group.label !== null && collapsed.has(group.key);
          return (
            <div key={group.key}>
              {group.label && (
                <button
                  type="button"
                  onClick={() => toggleGroup(group.key)}
                  className="flex items-center justify-between w-full px-3 py-1 text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 uppercase tracking-wider"
                  aria-expanded={!isCollapsed}
                >
                  <span>{group.label}</span>
                  <ChevronDown
                    className={`w-3 h-3 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
                  />
                </button>
              )}
              {!isCollapsed && (
                <div className="space-y-0.5 mt-1">
                  {group.items.map(({ to, icon: Icon, label, hint }) => {
                    const badgeConfig = BADGE_FOR_ROUTE[to];
                    const badgeCount = badgeConfig ? counts[badgeConfig.key] : 0;
                    const pinned = isPinned(to);
                    return (
                      <div key={to} className="group/navrow relative">
                        <NavLink
                          to={to}
                          end={to === '/'}
                          onClick={onNavigate}
                          title={hint}
                          className={({ isActive }) => navLinkCls(isActive)}
                        >
                          <Icon className="w-5 h-5 shrink-0" />
                          <span className="flex-1">{label}</span>
                          {badgeConfig && (
                            <NavBadge count={badgeCount} variant={badgeConfig.variant} />
                          )}
                        </NavLink>
                        {/* Star button: hover-reveal on hover-capable devices,
                            always-visible when already pinned, AND always-visible
                            on touch devices (no hover → @media(hover:none)) so
                            phone users can pin a first item (nav declutter 2026-06-15). */}
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(to); }}
                          className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded transition-opacity ${
                            pinned
                              ? 'text-amber-500 hover:text-amber-600 opacity-100'
                              : 'text-slate-400 hover:text-amber-500 opacity-0 group-hover/navrow:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-100'
                          }`}
                          aria-label={pinned ? `Unpin ${label}` : `Pin ${label}`}
                          title={pinned ? 'Unpin' : 'Pin to top'}
                        >
                          <Star className="w-3.5 h-3.5" fill={pinned ? 'currentColor' : 'none'} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Sprint 1.5 (2026-05-28): Admin moved OUT of sidebar to topbar
            avatar dropdown (UserDropdown.tsx). Rarely-touched, doesn't
            deserve permanent sidebar real estate. */}
      </nav>

      {/* Nav declutter (2026-06-15): Settings demoted from a 10th sidebar group
          to a single footer link. Its 8 sub-pages stay reachable on the
          /settings index page + via Cmd+P (still in TASK_LAYOUT). */}
      <div className="shrink-0 px-3 pt-2 border-t border-slate-200 dark:border-slate-700">
        <NavLink
          to="/settings"
          onClick={onNavigate}
          className={({ isActive }) => navLinkCls(isActive)}
        >
          <Settings className="w-5 h-5 shrink-0" />
          <span className="flex-1">Settings</span>
        </NavLink>
      </div>

      {/* Sprint 1.5 footer — identity card + 3-state theme toggle. */}
      <SidebarUserFooter />
    </aside>
  );
}
